import type { SprintProgress, TicketProgress } from '@shared/schemas';

/**
 * Pure helpers reading the Q-1 SprintProgress.startedAt extension (already
 * existed pre-CHAT_2B per QuizAggregationWidget · CHAT_2B Q-1 picked «adopt
 * as official source» 16 MAY).
 *
 * Designed для future Sprint.tsx «День N / 7» header consumer · isolated from
 * foreign-entangled working tree. Provides:
 *   - sprint.startedAt accessor с fallback к MIN(ticket.startedAt)
 *   - days-elapsed calculation respecting skipped days (pause/resume scenarios)
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Short sprint name for compact headers — drops the «— tagline» half of a
 * sprint title (e.g. «Requirements Reading — читаем ТЗ как QA» → «Requirements
 * Reading»). Used by the FinalTest header and the Dashboard plan heading so the
 * data-driven sprint title stays readable and consistent across screens.
 */
export function shortSprintTitle(title: string): string {
  const dashIdx = title.indexOf('—');
  return dashIdx > 0 ? title.slice(0, dashIdx).trim() : title.trim();
}

/* ─── Q-1 · sprint startedAt resolution ──────────────────────────────────── */

/**
 * Resolve effective sprint start ISO. Prefers explicit `sprint.startedAt`
 * (per Q-1 design) · falls back к MIN(ticket.startedAt) across all ticket
 * progress in the sprint · returns null если nothing recorded.
 *
 * Per CHAT_2B Q-1: «replaces current derivation from MIN(ticket.startedAt)»
 * but graceful-degrade preserves Phase H ride-along migration window when
 * old user data lacks the explicit field.
 */
export function resolveSprintStartIso(
  sprint: Pick<SprintProgress, 'startedAt'> | undefined,
  ticketsInSprint: Array<Pick<TicketProgress, 'startedAt'>>,
): string | null {
  if (sprint?.startedAt) return sprint.startedAt;
  const ticketStarts = ticketsInSprint
    .map((t) => t.startedAt)
    .filter((s): s is string => Boolean(s))
    .filter((s) => !Number.isNaN(new Date(s).getTime()));
  if (ticketStarts.length === 0) return null;
  return ticketStarts.reduce((min, cur) => (cur < min ? cur : min));
}

/**
 * Days since sprint started. 1-indexed: day 1 = sprint start day · day 2 =
 * next day · etc. Per «День N / 7» Sprint header convention. Returns null
 * если no start recorded. Clamps к min 1 (negative values from clock skew).
 *
 * `total` param defaults к 7 (typical sprint length) но caller can override
 * для S0 (3-day onboarding) / S18 finale / etc.
 */
export function sprintDayNumber(
  startIso: string | null,
  now: Date = new Date(),
): number | null {
  if (!startIso) return null;
  const t = new Date(startIso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = now.getTime() - t;
  // 1-indexed: same day = day 1
  return Math.max(1, Math.floor(diffMs / MS_PER_DAY) + 1);
}

/**
 * Composite helper · resolves sprint start + computes current day in one call.
 * Returns `{ startIso · dayNumber }` shape для convenient destructuring in
 * Sprint.tsx header consumer.
 */
export function sprintProgressInDays(
  sprint: Pick<SprintProgress, 'startedAt'> | undefined,
  ticketsInSprint: Array<Pick<TicketProgress, 'startedAt'>>,
  now: Date = new Date(),
): { startIso: string | null; dayNumber: number | null } {
  const startIso = resolveSprintStartIso(sprint, ticketsInSprint);
  const dayNumber = sprintDayNumber(startIso, now);
  return { startIso, dayNumber };
}
